// ProgramTab — the Onboarding page (/dashboard/onboarding). A program is a
// reusable group of moduler (modules) you assign to employees. Master-detail: pick
// a program on the left → see its Moduler + assigned MEDARBETARE (and assign more)
// on the right. A dismissible explainer guides what a program is + the flow.
// The course LIBRARY lives in its own first-class area now ("Moduler",
// /dashboard/courses). (Onboarding redesign #216 — see ONBOARDING_REDESIGN.md.)
import React, { useEffect, useState } from 'react';
import { Users, Plus, Star, UserPlus, Trash2, Pencil, GraduationCap, BookOpen, Clock, Lightbulb, ArrowRight, Calendar } from 'lucide-react';
import { onboardingApi } from '@/lib/api/onboardingApi';
import { OnboardingProgram, ProgramEmployee } from '@/lib/models/onboarding';
import { Module } from '@/lib/models/module';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { ActionButton, OutlineButton } from '@/components/custom-ui/action-button';
import { StatusBadge } from '@/components/custom-ui/status-badge';
import { IconChip } from '@/components/custom-ui/IconChip';
import { UserAvatar } from '@/components/custom-ui/UserAvatar';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import { NEXILINK_CARD_WHITE, NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_ROW, NEXILINK_SPACING, NEXILINK_GAP } from '@/lib/nexilink-card-styles';
import { sumMinutes, formatMinutes } from '@/lib/duration';
import ProgramFormModal from './ProgramFormModal';
import AssignOnboardingModal from './AssignOnboardingModal';

const fmtDate = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('sv-SE');
};

const ProgramTab: React.FC = () => {
    const [programs, setPrograms] = useState<OnboardingProgram[]>([]);
    const [selectedProgram, setSelectedProgram] = useState<OnboardingProgram | null>(null);
    const [employees, setEmployees] = useState<ProgramEmployee[]>([]);
    const [assignedCounts, setAssignedCounts] = useState<Record<string, number>>({});
    const [moduleMap, setModuleMap] = useState<Record<string, Module>>({});
    const [removingId, setRemovingId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingProgram, setEditingProgram] = useState<OnboardingProgram | null>(null);
    const [showAssignModal, setShowAssignModal] = useState(false);

    useEffect(() => {
        loadPrograms();
        onboardingApi.getOnboardingModules()
            .then((r) => {
                const map: Record<string, Module> = {};
                (r.modules || []).forEach((m) => { map[m.id] = m; });
                setModuleMap(map);
            })
            .catch(() => { /* program detail just shows ids-as-count if this fails */ });
    }, []);

    const loadPrograms = async () => {
        setIsLoading(true);
        try {
            const result = await onboardingApi.getPrograms();
            setPrograms(result.programs || []);
            setAssignedCounts(result.assigned_counts || {});
            if (result.programs?.length > 0) {
                await selectProgram(result.programs[0]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const selectProgram = async (program: OnboardingProgram) => {
        setSelectedProgram(program);
        try {
            const result = await onboardingApi.getProgramEmployees(program.id);
            setEmployees(result.employees || []);
        } catch {
            setEmployees([]);
        }
    };

    const handleDeleteProgram = async (program: OnboardingProgram, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm(`Radera programmet "${program.name}"? Alla tilldelningar till programmet tas bort.`)) return;
        try {
            await onboardingApi.deleteProgram(program.id);
            if (selectedProgram?.id === program.id) {
                setSelectedProgram(null);
                setEmployees([]);
            }
            await loadPrograms();
        } catch (err) {
            console.error('[ProgramTab] delete program failed:', err);
        }
    };

    const handleUnassign = async (emp: ProgramEmployee) => {
        if (!selectedProgram) return;
        if (!window.confirm(`Ta bort ${emp.name || 'medarbetaren'} från "${selectedProgram.name}"?`)) return;
        setRemovingId(emp.id);
        try {
            await onboardingApi.unassignOnboarding(emp.id);
            setEmployees((prev) => prev.filter((e) => e.id !== emp.id));
        } catch (e) {
            console.error('[ProgramTab] unassign failed:', e);
        } finally {
            setRemovingId(null);
        }
    };

    const modulesOf = (program: OnboardingProgram): Module[] =>
        (program.module_ids || []).map((id) => moduleMap[id]).filter(Boolean) as Module[];

    if (isLoading) {
        return <LoadingState message="Laddar program..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            {/* Guidance — what a program is + the 3-step flow. Collapsible (reusable). */}
            <ExplainerCard id="onboarding-program" icon={Lightbulb} tone="amber" title="Så funkar onboarding">
                <p className="text-sm text-gray-600 mt-0.5 mb-3">
                    Ett <strong>program</strong> är en återanvändbar onboarding-resa — en grupp <strong>moduler</strong> du tilldelar nyanställda.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1.5 text-sm">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700">
                        <BookOpen className="w-4 h-4 text-[#1A4D4F]" /> 1. Skapa moduler under Moduler
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-300 hidden sm:block" />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700">
                        <GraduationCap className="w-4 h-4 text-[#1A4D4F]" /> 2. Gruppera dem i ett program
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-300 hidden sm:block" />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700">
                        <Users className="w-4 h-4 text-[#1A4D4F]" /> 3. Tilldela till medarbetare
                    </span>
                </div>
            </ExplainerCard>

            <div className="flex items-center justify-end">
                <ActionButton icon={Plus} onClick={() => setShowCreateModal(true)}>
                    Nytt Program
                </ActionButton>
            </div>

            {programs.length === 0 ? (
                <div className={`${NEXILINK_CARD_WHITE} p-12 text-center`}>
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#A4C3B2]/30 rounded-full flex items-center justify-center">
                        <GraduationCap className="w-8 h-8 text-[#1A4D4F]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Inga program ännu</h3>
                    <p className="text-gray-500 mb-6">Skapa ett program — en återanvändbar grupp av moduler — och tilldela det till nyanställda.</p>
                    <ActionButton icon={Plus} onClick={() => setShowCreateModal(true)}>Nytt Program</ActionButton>
                </div>
            ) : (
                <div className={`grid grid-cols-1 lg:grid-cols-3 ${NEXILINK_GAP}`}>
                    {/* Program list */}
                    <div className={NEXILINK_SPACING}>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Program</h2>
                            <p className="text-xs text-gray-400 mt-0.5">Onboarding-spår för dina medarbetare</p>
                        </div>
                        <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                            <div className="px-2 py-2 divide-y divide-gray-50">
                                {programs.map((program) => (
                                    <div
                                        key={program.id}
                                        onClick={() => selectProgram(program)}
                                        className={`${NEXILINK_ROW} cursor-pointer ${
                                            selectedProgram?.id === program.id ? 'bg-[#A4C3B2]/30 hover:bg-[#A4C3B2]/30' : ''
                                        }`}
                                    >
                                        <IconChip icon={GraduationCap} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-900 truncate">{program.name}</p>
                                            <p className="text-sm text-gray-500">
                                                {program.module_ids?.length || 0} moduler · {assignedCounts[program.id] ?? 0} tilldelade
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            {program.is_default && (
                                                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                                            )}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingProgram(program); }}
                                                title="Redigera program"
                                                className="p-2 rounded-lg text-gray-400 hover:text-[#1A4D4F] hover:bg-[#A4C3B2]/30 transition-colors"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => handleDeleteProgram(program, e)}
                                                title="Radera program"
                                                className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Selected program detail — its moduler + medarbetare */}
                    <div className={`lg:col-span-2 ${NEXILINK_SPACING}`}>
                        {selectedProgram ? (
                            <>
                                {/* Moduler i programmet */}
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <h2 className="text-lg font-semibold text-gray-900">Moduler i {selectedProgram.name}</h2>
                                        <p className="text-xs text-gray-400 flex items-center gap-2 mt-0.5 whitespace-nowrap">
                                            {(() => {
                                                const mods = modulesOf(selectedProgram);
                                                const total = sumMinutes(mods.map((m) => m.estimated_duration));
                                                return (
                                                    <span>{mods.length} {mods.length === 1 ? 'modul' : 'moduler'}{total > 0 && ` · ~${formatMinutes(total)} totalt`}</span>
                                                );
                                            })()}
                                            <span className="flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {selectedProgram.updated_at
                                                    ? `Uppdaterad ${fmtDate(selectedProgram.updated_at)}`
                                                    : `Skapad ${fmtDate(selectedProgram.created_at)}`}
                                            </span>
                                        </p>
                                    </div>
                                    <OutlineButton icon={Pencil} onClick={() => setEditingProgram(selectedProgram)}>
                                        Redigera
                                    </OutlineButton>
                                </div>
                                {(() => {
                                    const mods = modulesOf(selectedProgram);
                                    if (mods.length === 0) {
                                        return (
                                            <div className={`${NEXILINK_CARD_WHITE} p-6 text-center`}>
                                                <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                                <p className="text-gray-500 text-sm">Inga moduler i detta program ännu. Lägg till moduler via "Redigera".</p>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                                            <div className="px-2 py-2 divide-y divide-gray-50">
                                                {mods.map((m) => (
                                                    <div key={m.id} className={NEXILINK_ROW}>
                                                        <IconChip icon={BookOpen} tone="brand" />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-semibold text-gray-900 truncate">{m.module_name}</p>
                                                            {m.module_description && (
                                                                <p className="text-sm text-gray-500 truncate">{m.module_description}</p>
                                                            )}
                                                        </div>
                                                        {m.estimated_duration && (
                                                            <span className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                                                                <Clock className="w-3.5 h-3.5" /> {m.estimated_duration}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Medarbetare */}
                                <div className="flex items-center justify-between pt-2">
                                    <h2 className="text-lg font-semibold text-gray-900">Medarbetare</h2>
                                    <OutlineButton icon={UserPlus} onClick={() => setShowAssignModal(true)}>
                                        Tilldela
                                    </OutlineButton>
                                </div>
                                {employees.length > 0 ? (
                                    <div className={NEXILINK_CARD_WHITE_ELEVATED}>
                                        <div className="px-2 py-2 divide-y divide-gray-50">
                                            {employees.map((emp) => (
                                                <div key={emp.id} className={NEXILINK_ROW}>
                                                    <UserAvatar name={emp.name || 'Okänd medarbetare'} pictureUrl={emp.picture_url} size="sm" />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">{emp.name || 'Okänd medarbetare'}</p>
                                                        <p className="text-sm text-gray-500 truncate">
                                                            {emp.email ? `${emp.email} · ` : ''}
                                                            Tilldelad {new Date(emp.assigned_at).toLocaleDateString('sv-SE')}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        <StatusBadge status={emp.status} />
                                                        <button
                                                            onClick={() => handleUnassign(emp)}
                                                            disabled={removingId === emp.id}
                                                            title="Ta bort tilldelning"
                                                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className={`${NEXILINK_CARD_WHITE} p-8 text-center`}>
                                        <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                                        <p className="text-gray-500">Inga medarbetare tilldelade detta program ännu.</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className={`${NEXILINK_CARD_WHITE} p-8 text-center`}>
                                <p className="text-gray-500">Välj ett program till vänster för att se moduler och medarbetare.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showCreateModal && (
                <ProgramFormModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={() => { setShowCreateModal(false); loadPrograms(); }}
                />
            )}

            {editingProgram && (
                <ProgramFormModal
                    program={editingProgram}
                    onClose={() => setEditingProgram(null)}
                    onSuccess={() => {
                        const id = editingProgram.id;
                        setEditingProgram(null);
                        onboardingApi.getPrograms().then((res) => {
                            setPrograms(res.programs || []);
                            const updated = (res.programs || []).find((p) => p.id === id);
                            if (updated) selectProgram(updated);
                        });
                    }}
                />
            )}

            {showAssignModal && selectedProgram && (
                <AssignOnboardingModal
                    program={selectedProgram}
                    onClose={() => setShowAssignModal(false)}
                    onSuccess={() => { setShowAssignModal(false); selectProgram(selectedProgram); }}
                />
            )}
        </div>
    );
};

export default ProgramTab;
